import { useMutation, useQueryClient } from '@tanstack/react-query';
import { scenarioVersionInternalApi } from '../../apis';
import { InternalApiScenarioVersionCommand } from '../../api-internal';
import { useMe } from '../../organisation/useMe';
import { SCENARIO_QUERY_KEY } from '../scenarioQueryActions';

export const SCENARIO_VERSION_QUERY_KEY = 'scenario_version';

export const useCreateScenarioVersion = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  return useMutation({
    mutationFn: ({
      internalApiScenarioVersionCommand,
      scenarioId,
    }: {
      scenarioId: string;
      internalApiScenarioVersionCommand: InternalApiScenarioVersionCommand;
    }) => {
      return scenarioVersionInternalApi.createScenarioVersion({
        internalApiScenarioVersionCommand,
        scenarioId,
      });
    },
    onSuccess: async (version, { scenarioId }) => {
      queryClient.setQueryData([SCENARIO_VERSION_QUERY_KEY, version.id, currentOrganisation?.id], version);

      await queryClient.invalidateQueries({
        queryKey: [SCENARIO_QUERY_KEY, scenarioId, currentOrganisation?.id],
      });
    },
  });
};

export const useDuplicateScenarioVersion = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  return useMutation({
    mutationFn: ({ scenarioId, id }: { scenarioId: string; id: string }) => {
      return scenarioVersionInternalApi.duplicateScenarioVersion({
        id,
        scenarioId,
      });
    },
    onSuccess: async (version, { scenarioId }) => {
      queryClient.setQueryData([SCENARIO_VERSION_QUERY_KEY, version.id, currentOrganisation?.id], version);

      await queryClient.invalidateQueries({
        queryKey: [SCENARIO_QUERY_KEY, scenarioId, currentOrganisation?.id],
      });
    },
  });
};

export const useUpdateScenarioVersion = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  return useMutation({
    mutationFn: ({
      scenarioId,
      id,
      internalApiScenarioVersionCommand,
    }: {
      scenarioId: string;
      id: string;
      internalApiScenarioVersionCommand: InternalApiScenarioVersionCommand;
    }) => {
      return scenarioVersionInternalApi.updateScenarioVersion({
        scenarioId,
        id,
        internalApiScenarioVersionCommand,
      });
    },
    onSuccess: async (version, { scenarioId }) => {
      queryClient.setQueryData([SCENARIO_VERSION_QUERY_KEY, version.id, currentOrganisation?.id], version);

      await queryClient.invalidateQueries({
        queryKey: [SCENARIO_QUERY_KEY, scenarioId, currentOrganisation?.id],
      });
    },
  });
};

export const useDeleteScenarioVersion = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  return useMutation({
    mutationFn: ({ id, scenarioId }: { scenarioId: string; id: string }) => {
      return scenarioVersionInternalApi.deleteScenarioVersion({
        scenarioId,
        id,
      });
    },
    onSuccess: async (_, { id, scenarioId }) => {
      await queryClient.invalidateQueries({
        queryKey: [SCENARIO_VERSION_QUERY_KEY, id, currentOrganisation?.id],
      });

      await queryClient.invalidateQueries({
        queryKey: [SCENARIO_QUERY_KEY, scenarioId, currentOrganisation?.id],
      });
    },
  });
};
